/**
 * AssistantLauncher - the always-visible Assistant FAB (perf round,
 * audit #3).
 *
 * GlobalChrome used to statically import AssistantChat, which drags
 * lib/assistant.ts and the whole staff planner (~200 kB of source) into
 * the critical bundle on EVERY page for EVERY role, even though the panel
 * is closed until somebody taps the button. This file keeps only the tiny
 * FAB inline; the panel body (AssistantChat.tsx) is a React.lazy chunk
 * fetched the first time the panel opens, behind a small StudioLoading
 * fallback.
 *
 * BACK-BUTTON HANDLING (found by the same review that fixed NavDrawer,
 * PR #453/#455 - see that file's own doc comment for the full mechanics).
 * The chat panel is a backdrop-plus-dialog overlay with Escape and an
 * explicit close button, the identical shape NavDrawer had, and it had the
 * identical gap: no tie to browser history at all. A physical Back (Android
 * gesture, iOS edge swipe, the hardware key) fell straight through to the
 * page underneath, leaving the panel visually stuck open over the new page
 * - arguably worse than the drawer's old bug, which at least closed itself
 * on the resulting pathname change.
 *
 * The fix is the same marker-entry mechanism NavDrawer uses, moved here
 * rather than into AssistantChat.tsx: AssistantChatPanel is a React.lazy
 * chunk that mounts and unmounts with `open` (that is the whole point of
 * the perf split above), so it cannot be the thing watching history - a
 * marked entry resurrected by Forward while the panel is CLOSED would have
 * nothing listening to neutralize it. AssistantLauncher, like NavDrawer
 * itself, is mounted for the app's whole life (module-level, not
 * per-render), so it is the one place that can own this safely. See the
 * effects below for the three jobs, and NavDrawer.tsx's own comment block
 * for why each one exists and the two traps ("history.length is the wrong
 * assertion" and "the neutralizer can race the pop-on-close flow") that
 * cost real time there. Proven with a browser harness before this comment
 * was written - see hub/tests/harness/assistant-chat.
 */

import { Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Icon } from './Icon';
import StudioLoading from './StudioLoading';
import { SPRING_SNAPPY } from '../lib/motion';
// Same stale-chunk protection as every route in App.tsx - see
// hub/src/lib/lazyRoute.ts's doc comment.
import { lazyRoute } from '../lib/lazyRoute';

const AssistantChatPanel = lazyRoute(() => import('./AssistantChat'));

// position: 'absolute' is deliberate - this button is rendered inside
// .ldg-fab-layer (see index.css), a position:fixed wrapper anchored with
// `bottom: 0` so its own bottom edge is the browser's live, dynamically
// tracked viewport bottom (including iOS Safari's animated address bar) by
// definition - no unit or JS math involved for the resting position, only
// fabViewportTracking.ts's small, clamped upward nudge on top of it.
const floatingButtonStyle: React.CSSProperties = {
  position: 'absolute',
  right: 'calc(env(safe-area-inset-right, 0px) + 14px)',
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 74px)',
  zIndex: 920,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  minHeight: 44,
  padding: '0 14px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--silver-edge)',
  background: 'linear-gradient(180deg, var(--paper) 0%, var(--off-white) 100%)',
  color: 'var(--ink)',
  boxShadow: '0 14px 30px rgba(35,28,20,0.14), inset 0 1px 0 rgba(255,255,255,0.62)',
  cursor: 'pointer',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 800,
};

function isCompactScreen(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 520;
}

/** Chunk-loading fallback: same fixed backdrop the panel uses, with the
 *  shared studio loader inside, so tapping the FAB on a cold cache shows
 *  motion immediately instead of a dead button. */
function PanelLoadingFallback() {
  return (
    <div
      // Same backdrop as the panel it stands in for, so it needs the same
      // print exemption. Missing it here would print a translucent sheet
      // over the page for exactly the cold-cache moment this exists to cover.
      className="no-print"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: 'rgba(23, 20, 16, 0.32)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: 'var(--space-4)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          background: 'var(--paper)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--line)',
          padding: 'var(--space-4)',
        }}
      >
        <StudioLoading label="Opening the Assistant" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Back-button handling. Same marker-entry approach as NavDrawer.tsx - see
// that file's own long comment block for the full reasoning on each job.
// Kept as a separate key from the drawer's own marker so the two overlays
// can be open at once (each owns its own pushed entry) without either
// stomping the other's state.
// ---------------------------------------------------------------------------
const ASSISTANT_HISTORY_MARKER = 'ldgAssistantPanelOpen';

function hasAssistantMarker(state: unknown): boolean {
  return (
    !!state &&
    typeof state === 'object' &&
    (state as Record<string, unknown>)[ASSISTANT_HISTORY_MARKER] === true
  );
}

function withAssistantMarker(state: unknown): Record<string, unknown> {
  const base = state && typeof state === 'object' ? (state as Record<string, unknown>) : {};
  return { ...base, [ASSISTANT_HISTORY_MARKER]: true };
}

/** The inverse of withAssistantMarker - strips the marker key entirely
 *  rather than setting it false, so a neutralized entry's state is
 *  byte-for-byte what it would have been had the panel never touched it. */
function withoutAssistantMarker(state: unknown): Record<string, unknown> {
  const base = state && typeof state === 'object' ? (state as Record<string, unknown>) : {};
  if (!(ASSISTANT_HISTORY_MARKER in base)) return base;
  const next = { ...base };
  delete next[ASSISTANT_HISTORY_MARKER];
  return next;
}

export default function AssistantLauncher() {
  const reduce = useReducedMotion();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(isCompactScreen);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setCompact(isCompactScreen());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // "Latest" refs so the history effects below can read the current open
  // state and location without listing them as reactive dependencies - see
  // NavDrawer.tsx's identical refs for exactly why that matters.
  const openRef = useRef(open);
  openRef.current = open;
  const locationRef = useRef(location);
  locationRef.current = location;
  const prevPathRef = useRef(location.pathname);
  // True for as long as WE hold a history entry pushed for the open panel.
  const ownsHistoryEntryRef = useRef(false);
  const wasOpenRef = useRef(open);
  // Whether the entry we're about to push (or just pushed) still carries the
  // marker, tracked across renders so the location effect can tell a REAL
  // transition (marker present, now gone) from a re-run against a location
  // that has not moved.
  const prevMarkerRef = useRef(hasAssistantMarker(location.state));
  // Set by a panel link's click handler (see AssistantChat.tsx's
  // onLinkClick prop) when it wants the actual jump to wait until our
  // pushed entry is confirmed gone, so the destination page never has that
  // entry sitting behind it as a phantom Back stop.
  const pendingHrefRef = useRef<string | null>(null);
  // True for the whole window between us calling navigate(-1) to pop our
  // OWN entry (closing via anything but a real Back) and that pop actually
  // landing - see NavDrawer.tsx's popInFlightRef for the race this guards.
  const popInFlightRef = useRef(false);

  // Opening pushes one history entry carrying a marker in its state, so a
  // physical Back has something of ours to consume instead of falling
  // through to the page underneath.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;
    if (open && !wasOpen && !ownsHistoryEntryRef.current) {
      ownsHistoryEntryRef.current = true;
      const loc = locationRef.current;
      navigate(`${loc.pathname}${loc.search}${loc.hash}`, {
        state: withAssistantMarker(loc.state),
      });
    } else if (!open && wasOpen && ownsHistoryEntryRef.current) {
      // Closing by any means OTHER than a real Back (the X, the backdrop,
      // Escape, or a link tap via handleLinkClick below). Pop our own entry
      // so it never sits there as a phantom Back stop. Skipped when a real
      // Back already consumed it - the location effect below clears
      // ownsHistoryEntryRef in that case before this ever runs.
      ownsHistoryEntryRef.current = false;
      popInFlightRef.current = true;
      navigate(-1);
    }
  }, [open, navigate]);

  // Fires on every real navigation (mount, and any push or pop), not just
  // when `open` changes - see NavDrawer.tsx's identical effect for why that
  // distinction matters. Same three jobs, in the same order:
  //  1. Landed on a marked entry we do NOT own - neutralize it in place
  //     before it can be mistaken for a live one.
  //  2. A real Back/Forward that lands on an entry without our marker while
  //     we still think we own one means the browser already popped it for
  //     us - sync the panel closed and stop there, no further navigation.
  //  3. Close whenever the route changes underneath the panel for some
  //     other reason while it's open (a redirect, a notification deep
  //     link) - the "partial mitigation" the drawer had before its own
  //     history fix, restored here as a defensive second layer rather than
  //     the whole fix.
  useEffect(() => {
    const nowMarked = hasAssistantMarker(location.state);
    const wasMarked = prevMarkerRef.current;
    if (nowMarked && !ownsHistoryEntryRef.current && !popInFlightRef.current) {
      prevMarkerRef.current = false;
      navigate(`${location.pathname}${location.search}${location.hash}`, {
        replace: true,
        state: withoutAssistantMarker(location.state),
      });
      return;
    }
    prevMarkerRef.current = nowMarked;
    if (wasMarked && !nowMarked) {
      ownsHistoryEntryRef.current = false;
      popInFlightRef.current = false;
      if (openRef.current) setOpen(false);
      if (pendingHrefRef.current) {
        const href = pendingHrefRef.current;
        pendingHrefRef.current = null;
        window.location.href = href;
      }
    }
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      if (openRef.current) setOpen(false);
    }
  }, [location, navigate]);

  const close = () => {
    setOpen(false);
    window.setTimeout(() => buttonRef.current?.focus(), 0);
  };

  // A link tapped inside the panel (see AssistantChat.tsx's delegated
  // handler). Pops our own entry BEFORE the page changes, never left behind
  // for a later Back to land on and reopen the panel - the same reason
  // NavDrawer's handleDrawerLinkClick exists.
  const handleLinkClick = (href: string) => {
    if (ownsHistoryEntryRef.current) {
      pendingHrefRef.current = href;
      close();
    } else {
      close();
      window.location.href = href;
    }
  };

  return (
    <>
      {/* .ldg-fab-layer (index.css): a viewport-stable, bottom:0-anchored
          wrapper so this button does not float away from the true bottom
          edge during iOS Safari's address-bar animation. zIndex matches
          the button's own prior fixed z-index (920) so its stacking order
          against NavDrawer etc is unchanged. */}
      <div className="ldg-fab-layer" style={{ zIndex: 920 }}>
        <motion.button
          ref={buttonRef}
          type="button"
          aria-label="Open the Studio Assistant"
          onClick={() => setOpen(true)}
          whileTap={reduce ? undefined : { scale: 0.97 }}
          transition={SPRING_SNAPPY}
          style={{
            ...floatingButtonStyle,
            width: compact ? 48 : undefined,
            padding: compact ? 0 : floatingButtonStyle.padding,
          }}
        >
          <Icon name="chat-bubble" size={16} />
          <span style={{ display: compact ? 'none' : undefined }}>Assistant</span>
        </motion.button>
      </div>

      {open && (
        <Suspense fallback={<PanelLoadingFallback />}>
          <AssistantChatPanel onClose={close} onLinkClick={handleLinkClick} />
        </Suspense>
      )}
    </>
  );
}
